import { useState, useCallback } from 'react'
import { useReportsContext, useStatus } from '../context' // Adjust path if needed
import executionReportsService from '../services/execution-reports.service'

/**
 * Custom hook to encapsulate report-related actions and their associated state.
 * This includes opening/closing assignment and review modals, and elevating reports.
 *
 * @returns {{
 *   reportData: object | null,
 *   reportDataReview: object | null,
 *   openModalState: boolean,
 *   openReviewModalState: boolean,
 *   openAssignModal: (report: object) => void,
 *   closeAssignModal: () => void,
 *   openReviewModal: (report: object) => void,
 *   closeReviewModal: () => void,
 *   elevarReport: (report: object) => void
 * }}
 */
export const useReportActions = () => {
    const { setstatus } = useStatus()
    const { elevarReporte, removeReportFromReportsToShow, updateNumbersLocally, reloadCurrentPage, valueFilter } = useReportsContext()
    const [reportData, setReportData] = useState(null)
    const [reportDataReview, setReportDataReview] = useState(null)
    const [openModalState, setOpenModalState] = useState(false) // For AssignDialog
    const [openReviewModalState, setOpenReviewModalState] = useState(false) // For ReviewReportDialog

    // Assign Modal Handlers
    const openAssignModal = useCallback((report) => {
        setReportData(report)
        setOpenModalState(true)
    }, [])

    const closeAssignModal = useCallback(() => {
        setReportData(null)
        setOpenModalState(false)
    }, [])

    // Review Modal Handlers
    const openReviewModal = useCallback(async (report) => {
        try {
            const executionReport = await executionReportsService.getExecutionReportByReportId(report._id)
            setReportDataReview({ ...report, execution: executionReport })
        } catch (error) {
            // If there is an error (e.g., 404), it means there is no execution report.
            // We can still open the modal with the main report data.
            console.warn(`No execution report found for report ${report._id}. Opening review modal without it.`)
            setReportDataReview(report)
        } finally {
            setOpenReviewModalState(true)
        }
    }, [])

    const closeReviewModal = useCallback(() => {
        setReportDataReview(null)
        setOpenReviewModalState(false)
    }, [])

    // Elevar Report Action
    const elevarReport = useCallback(async (report) => {
        if (window.confirm('¿Confirma que desea elevar la OT ' + (report.newIdIndex || report.idIndex) + ' para permitir su asignación?')) {
            const isSearching = !!(valueFilter && valueFilter.trim() !== '')
            const payload = {
                _id: report._id,
                origen: false,
                state: 'Asignar'
            }

            try {
                if (!isSearching) {
                    removeReportFromReportsToShow(report._id)
                }

                setstatus({
                    status: 'generating',
                    title: 'Elevando OT',
                    message: `Elevando OT ${report.newIdIndex || report.idIndex}...`,
                    severity: 'info'
                })

                await elevarReporte(payload, true)

                setstatus({
                    status: 'completed',
                    title: 'OT Elevada',
                    message: `La OT ${report.newIdIndex || report.idIndex} ahora puede ser asignada.`,
                    severity: 'success'
                })
            } catch (error) {
                console.error('Error al elevar reporte:', error)
                setstatus({
                    status: 'error',
                    title: 'Error al Elevar',
                    message: `No se pudo elevar la OT ${report.newIdIndex || report.idIndex}.`,
                    severity: 'error',
                    errorDetails: {
                        code: error.response?.status || 'Error',
                        info: error.response?.data?.message || error.message
                    }
                })
            }
        }
    }, [elevarReporte, removeReportFromReportsToShow, updateNumbersLocally, setstatus])

    return {
        reportData,
        reportDataReview,
        openModalState,
        openReviewModalState,
        openAssignModal,
        closeAssignModal,
        openReviewModal,
        closeReviewModal,
        elevarReport
    }
}
